import type { CorrectiveAction, ActionFilters } from "./types";

const ARCHIVED_ACTIONS_KEY = "archived_corrective_actions";

export const getArchivedActions = (): Set<string> => {
	try {
		const stored = localStorage.getItem(ARCHIVED_ACTIONS_KEY);
		return new Set(stored ? JSON.parse(stored) : []);
	} catch {
		return new Set();
	}
};

export const setArchivedActions = (archivedIds: Set<string>): void => {
	try {
		localStorage.setItem(
			ARCHIVED_ACTIONS_KEY,
			JSON.stringify([...archivedIds]),
		);
	} catch (error) {
		console.error("Failed to save archived actions:", error);
	}
};

export const filterActions = (
	actions: CorrectiveAction[],
	filters: ActionFilters,
	archivedActionIds: Set<string>,
): CorrectiveAction[] => {
	return actions.filter((action) => {
		const isArchived = archivedActionIds.has(action.id);
		const isCompleted = action.status === "completed";

		if (filters.showAll) return true;
		if (filters.showArchived && isArchived) return true;
		if (filters.showCompleted && isCompleted && !isArchived) return true;
		if (!isCompleted && !isArchived) return true;

		return false;
	});
};

export const getActionPriorityIcon = (priority: number) => {
	if (priority <= 1) return { color: "text-green-600", label: "Low" };
	if (priority <= 3) return { color: "text-yellow-600", label: "Medium" };
	return { color: "text-red-600", label: "High" };
};

export const getActionStatusIcon = (status: CorrectiveAction["status"]) => {
	switch (status) {
		case "not_started":
			return { color: "text-gray-500", label: "Not Started" };
		case "in_progress":
			return { color: "text-blue-500", label: "In Progress" };
		case "completed":
			return { color: "text-green-500", label: "Completed" };
	}
};

export const calculateMaxPriority = (actions: CorrectiveAction[]): number => {
	return Math.max(0, ...actions.map((a) => a.priority));
};

export const calculatePriorityForNewNotStartedAction = (
	actions: CorrectiveAction[],
): number => {
	// Find all "not_started" actions, excluding priority 1 (reserved for top priority)
	const notStartedActions = actions.filter(
		(a) => a.status === "not_started" && a.priority !== 1,
	);

	if (notStartedActions.length === 0) {
		// If no "not_started" actions exist, check if priority 2 is available
		// If priority 1 exists (top priority), use 2, otherwise use maxPriority + 1
		const hasTopPriority = actions.some((a) => a.priority === 1);
		if (hasTopPriority) {
			return 2;
		}
		const maxPriority = calculateMaxPriority(actions);
		return maxPriority + 1;
	}

	// Find the minimum priority among "not_started" actions
	const minPriority = Math.min(...notStartedActions.map((a) => a.priority));

	// Assign priority 1 less than the minimum to put it at the top of "Not Started"
	// But ensure it's at least 2 (since 1 is reserved for top priority)
	const newPriority = Math.max(2, minPriority - 1);

	// If the calculated priority conflicts with existing actions of other statuses,
	// we still use it since actions are grouped by status and sorted within each group
	return newPriority;
};
